import React, { FC, useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Paper from "@mui/material/Paper";
import Typography from "@mui/material/Typography";
import { ConfirmDialog } from "@outpave/ds";
import moment from "moment";
import { Inline, Stack } from "components/layout/reusableLayouts";
import { useLogoForScheme } from "utils/themeContextHelper";
import LoadingWithoutModal from "components/common/LoadingWithoutModal";
import AcquirerSetup from "components/checkout/AcquirerSetup";
import ContactLoginDialog from "components/auth/ContactLoginDialog";
import { useContactAuth } from "hooks/useContactAuthContext";
import { formatCashFull } from "utils/formatCash";
import { SubscriptionStatus } from "enums/Subscription";
import { useManagedSubscription } from "hooks/useManagedSubscription";
import { intervalPeriod } from "utils/billingInterval";

const ManageSubscription: FC = () => {
  const { t } = useTranslation();
  const { token } = useParams<{ token: string }>();

  const {
    subscription,
    companyDetails,
    charges,
    setupSession,
    loading,
    error,
    invalidToken,
    load,
    startPaymentMethodUpdate,
    savePaymentMethod,
    cancel,
  } = useManagedSubscription(token);

  const [cancelOpen, setCancelOpen] = useState(false);
  const [signInOpen, setSignInOpen] = useState(false);
  const { isContactAuthenticated } = useContactAuth();

  useEffect(() => {
    load();
  }, [load]);

  const changePaymentMethod = useCallback(async () => {
    if (!isContactAuthenticated) {
      setSignInOpen(true);
      return;
    }

    const { signInRequired } = await startPaymentMethodUpdate();
    if (signInRequired) setSignInOpen(true);
  }, [isContactAuthenticated, startPaymentMethodUpdate]);

  if (invalidToken) {
    return (
      <ManagePage>
        <ManageCard>
          <Stack sx={{ gap: "var(--space-3)", textAlign: "center" }}>
            <Typography variant="h5">
              {t("manageSubscription.InvalidTitle")}
            </Typography>
            <Typography variant="body1">
              {t("manageSubscription.InvalidMessage")}
            </Typography>
          </Stack>
        </ManageCard>
      </ManagePage>
    );
  }

  const isCanceled = subscription?.status === SubscriptionStatus.CANCELED;
  const hasEnded =
    !!subscription &&
    [
      SubscriptionStatus.CANCELED,
      SubscriptionStatus.COMPLETED,
      SubscriptionStatus.SUPERSEDED,
    ].includes(subscription.status);

  return (
    <ManagePage>
      {subscription && (
        <Stack>
          <ManageCard>
            <Stack sx={{ gap: "var(--space-4)" }}>
              <Typography variant="h5">
                {companyDetails?.companyName || t("Your subscription")}
              </Typography>

              {subscription.planName && (
                <Typography variant="subtitle2">
                  {subscription.planName}
                </Typography>
              )}

              <Typography variant="stat">
                {t("manageSubscription.AmountPerInterval", {
                  amount: formatCashFull(
                    subscription.amount,
                    subscription.currency,
                  ),
                  interval: t(intervalPeriod(subscription.interval)),
                })}
              </Typography>

              {isCanceled ? (
                <Typography variant="body1">
                  {t(
                    subscription.paymentFailed
                      ? "manageSubscription.PaymentFailedStopped"
                      : "manageSubscription.CanceledMessage",
                  )}
                </Typography>
              ) : (
                <>
                  {subscription.nextBillingDate && (
                    <DetailRow
                      label={t("Next billing date")}
                      value={moment(subscription.nextBillingDate).format("ll")}
                    />
                  )}
                  {subscription.paymentMethodLast4 && (
                    <DetailRow
                      label={t("Payment method")}
                      value={`${subscription.paymentMethodBrand || ""} ****${subscription.paymentMethodLast4}`}
                    />
                  )}
                  {subscription.cancelAtPeriodEnd && (
                    <Typography variant="body2">
                      {t("manageSubscription.EndingAfterPeriod")}
                    </Typography>
                  )}
                </>
              )}
            </Stack>
          </ManageCard>

          {!hasEnded && (
            <ManageCard>
              <Stack>
                <Typography variant="subtitle2">
                  {t("Payment method")}
                </Typography>

                {setupSession ? (
                  <Stack>
                    {setupSession.savedPaymentMethods?.map((method) => (
                      <Inline key={method.id} justify="between">
                        <Typography variant="body2">
                          {method.brand ||
                            (method.methodType === "us_bank_account"
                              ? t("Bank account")
                              : t("Card"))}{" "}
                          ****{method.last4}
                        </Typography>
                        <Button
                          disabled={loading || method.current}
                          onClick={() => savePaymentMethod(method.id)}
                        >
                          {method.current
                            ? t("Current payment method")
                            : t("Use this payment method")}
                        </Button>
                      </Inline>
                    ))}
                    <Typography variant="body2">
                      {t("Add a new payment method")}
                    </Typography>
                    <AcquirerSetup
                      session={{
                        acquirer: setupSession.acquirer.acquirer,
                        clientSecret: setupSession.clientSecret,
                        publishableKey: setupSession.acquirer.publishableKey,
                        componentType: setupSession.acquirer.componentType,
                      }}
                      onSaved={async (paymentMethodId) => {
                        await savePaymentMethod(paymentMethodId);
                      }}
                    />
                  </Stack>
                ) : (
                  <Inline>
                    <Button
                      variant="default-outlined"
                      onClick={changePaymentMethod}
                    >
                      {t("Change payment")}
                    </Button>
                    <Button
                      variant="danger-soft"
                      onClick={() => setCancelOpen(true)}
                    >
                      {t("Cancel subscription")}
                    </Button>
                  </Inline>
                )}

                {error && (
                  <Typography variant="body2" color="error">
                    {t(error)}
                  </Typography>
                )}
              </Stack>
            </ManageCard>
          )}

          {!!charges.length && (
            <ManageCard>
              <Stack sx={{ gap: "var(--space-2)" }}>
                <Typography variant="subtitle2">
                  {t("Recent payments")}
                </Typography>
                {charges.map((charge) => (
                  <Inline
                    key={`${charge.period}-${charge.attempt}`}
                    justify="between"
                  >
                    <Typography variant="body2">
                      {charge.createdAt
                        ? moment(charge.createdAt).format("ll")
                        : charge.period}
                    </Typography>
                    <Typography variant="body2">
                      {formatCashFull(charge.amount, subscription.currency)}{" "}
                      {t(charge.status)}
                      {charge.declineCode &&
                        ` — ${charge.declineCode.replace(/_/g, " ")}`}
                    </Typography>
                  </Inline>
                ))}
              </Stack>
            </ManageCard>
          )}
        </Stack>
      )}

      <ConfirmDialog
        open={cancelOpen}
        tone="danger"
        title={t("Cancel subscription")}
        message={
          <Typography variant="body2">
            {t("manageSubscription.CancelConfirm")}
          </Typography>
        }
        confirmLabel={t("Yes, cancel")}
        cancelLabel={t("Keep it")}
        busy={loading}
        onConfirm={async () => {
          await cancel(false);
          setCancelOpen(false);
        }}
        onCancel={() => {
          if (loading) return;
          setCancelOpen(false);
        }}
      />

      <ContactLoginDialog
        open={signInOpen}
        email={subscription?.payerEmail}
        onAuthenticated={async () => {
          setSignInOpen(false);
          await load();
          await startPaymentMethodUpdate();
        }}
        onClose={() => setSignInOpen(false)}
      />

      <LoadingWithoutModal open={loading} />
    </ManagePage>
  );
};

const ManagePage: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const platformLogo = useLogoForScheme();

  return (
    <Box
      sx={{
        minHeight: "100vh",
        position: "relative",
        backgroundColor: "var(--color-bg-page)",
      }}
    >
      <Box
        sx={{
          position: "absolute",
          top: 0,
          left: 0,
          padding: "var(--space-6)",
        }}
      >
        <Box
          component="img"
          src={platformLogo}
          alt=""
          draggable={false}
          sx={{ height: 40, width: "auto", maxWidth: 200 }}
        />
      </Box>

      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          minHeight: "100vh",
          paddingTop: "var(--space-12)",
          paddingBottom: "var(--space-8)",
          paddingX: "var(--space-4)",
        }}
      >
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: "var(--space-5)",
            width: "100%",
            maxWidth: 500,
          }}
        >
          {children}
        </Box>
      </Box>
    </Box>
  );
};

const ManageCard: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <Paper
    sx={{
      display: "flex",
      flexDirection: "column",
      gap: "var(--space-4)",
      padding: "var(--space-6)",
    }}
  >
    {children}
  </Paper>
);

const DetailRow: React.FC<{ label: string; value: string }> = ({
  label,
  value,
}) => (
  <Inline justify="between">
    <Typography variant="body2" sx={{ color: "var(--color-text-secondary)" }}>
      {label}
    </Typography>
    <Typography variant="body2">{value}</Typography>
  </Inline>
);

export default ManageSubscription;
